import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormControlLabel, FormGroup, Grid, Switch } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  moment,
  type Nil,
  PuiDialog,
  useFields,
} from '@pbt/pbt-ui-components'

import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { ShipmentItemSearchCriteria } from '~/constants/shipments'
import { useInventoryItemTrackingItems } from '~/hooks/useInventoryItemTracking'
import type { Order } from '~/types'
import type { ShipmentItemUnion } from '~/utils/inventoryItemTrackingHelpers'
import { isWithinTimeInterval } from '~/utils/time'

import ChooseShipmentItemTable from './ChooseShipmentItemTable'

const isAnyItemExpiringSoon = (items: ShipmentItemUnion[]): boolean => {
  const now = moment()
  const thirtyDaysInMinutes = 30 * 24 * 60

  return items.some((item) =>
    isWithinTimeInterval(item.expirationDate, now, thirtyDaysInMinutes),
  )
}

const useStyles = makeStyles(
  (theme) => ({
    button: {
      height: 40,
      padding: theme.spacing(1, 6),
      margin: theme.spacing(1, 2),
    },
  }),
  { name: 'ChooseShipmentItemDialog' },
)

const SearchCriteriaValuePath = {
  [ShipmentItemSearchCriteria.INVENTORY]: ['variation', 'id'],
  [ShipmentItemSearchCriteria.PROCEDURE]: ['procedure', 'id'],
  [ShipmentItemSearchCriteria.PROCEDURE_WITH_VARIATION]: ['procedure', 'id'],
}

interface ChooseShipmentItemDialogProps extends BasePuiDialogProps {
  isMultiSelect?: boolean
  onSelect: (
    shipmentItems: ChooseShipmentItemDialogProps['isMultiSelect'] extends true
      ? ShipmentItemUnion[]
      : ShipmentItemUnion,
  ) => void
  order: Partial<Order> | Nil
  searchCriteria?: ShipmentItemSearchCriteria
  selectedItems?: Partial<ShipmentItemUnion>[]
  showToggle: boolean
}

const ChooseShipmentItemDialog = ({
  order,
  onSelect,
  isMultiSelect = false,
  open,
  onClose,
  showToggle,
  selectedItems,
  searchCriteria = ShipmentItemSearchCriteria.PROCEDURE,
}: ChooseShipmentItemDialogProps) => {
  const [includeExpiredItems, setIncludeExpiredItems] = useState(false)
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Dialogs'])

  const {
    inventoryItemTrackingItemsFromSorted,
    isLoading,
    totalCount,
    clear,
    loadMoreItems,
  } = useInventoryItemTrackingItems()

  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.CHOOSE_SHIPMENT_ITEM,
  })

  const searchCriteriaId = R.path<string>(
    SearchCriteriaValuePath[searchCriteria],
    order,
  )

  const handleLoadMoreItems = (from: number, to: number) => {
    if (searchCriteriaId) {
      loadMoreItems(
        searchCriteria,
        searchCriteriaId,
        from,
        to,
        includeExpiredItems,
      )
    }
  }

  const handleIncludeExpiredItemsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIncludeExpiredItems(event.target.checked)
  }

  useEffect(() => {
    clear()
    if (searchCriteriaId) {
      loadMoreItems(
        searchCriteria,
        searchCriteriaId,
        0,
        25,
        includeExpiredItems,
      )
    }
    return () => {
      clear()
    }
  }, [
    includeExpiredItems,
    clear,
    loadMoreItems,
    searchCriteria,
    searchCriteriaId,
  ])

  const titleText = `${t('Common:CHOOSE_FROM_INVENTORY')}: ${order?.name}`

  const title = (
    <Grid container alignItems="center" component="span" spacing={3}>
      <Grid item>{titleText}</Grid>
      {showToggle && (
        <Grid item>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={includeExpiredItems}
                  onChange={handleIncludeExpiredItemsChange}
                />
              }
              label={t('Common:INCLUDE_EXPIRED_ITEMS')}
            />
          </FormGroup>
        </Grid>
      )}
    </Grid>
  )

  const {
    fields: { chosenItem },
    validate,
  } = useFields([
    {
      name: 'chosenItem',
      label: t('Common:CHOSEN_ITEM'),
      validators: ['required'],
      initialValue: isMultiSelect ? selectedItems : null,
    },
  ])

  const noItemSelected = !chosenItem.value

  const handleConfirm = () => {
    onSelect(chosenItem.value)
    if (onClose) {
      onClose()
    }
  }

  const handleSelect = () => {
    const isExpirationDateApproaching = isMultiSelect
      ? isAnyItemExpiringSoon(chosenItem.value)
      : false
    if (validate()) {
      if (isExpirationDateApproaching && isMultiSelect) {
        openConfirmAlert({
          applyCustomMessage: true,
          message: t('Dialogs:DISMISSIBLE_ALERT.ARE_YOU_SURE'),
          content: t(
            'Dialogs:CHOOSE_SHIPMENT_ITEM_DIALOG.ONE_OF_YOUR_SELECTION',
          ),
          onConfirm: (proceed) => proceed && handleConfirm(),
          okButtonText: t('Common:YES'),
          cancelButtonText: t('Common:NO_I_DO_NOT_DONE'),
        })
      } else {
        handleConfirm()
      }
    }
  }

  return (
    <PuiDialog
      fullWidth
      actions={
        <ButtonWithLoader
          className={classes.button}
          disabled={isLoading || noItemSelected}
          loading={isLoading}
          type="submit"
          onClick={handleSelect}
        >
          {t('Common:SELECT_ACTION')}
        </ButtonWithLoader>
      }
      aria-labelledby="choose-from-inventory-dialog"
      maxWidth="lg"
      open={open}
      title={title}
      onClose={onClose}
    >
      <Grid container px={3} py={2}>
        <ChooseShipmentItemTable
          chosenItemField={chosenItem}
          isMultiSelect={isMultiSelect}
          loadMoreItems={handleLoadMoreItems}
          options={inventoryItemTrackingItemsFromSorted}
          totalCount={totalCount}
        />
      </Grid>
    </PuiDialog>
  )
}

export default ChooseShipmentItemDialog
